// Screen: "Landing"
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import Footer from '../components/Footer';

// Hardwood floor background style
const hardwoodFloorStyle = {
  background: `
    repeating-linear-gradient(90deg, #3d2817 0px, #4a3423 2px, #5c4033 4px, #4a3423 6px, #3d2817 8px),
    repeating-linear-gradient(0deg, transparent 0px, transparent 60px, rgba(0,0,0,0.15) 60px, rgba(0,0,0,0.15) 62px),
    linear-gradient(180deg, #4a3423 0%, #3d2817 50%, #2d1f12 100%)
  `,
  backgroundBlendMode: "overlay, overlay, normal"
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate('/home');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => { setInstalled(true); setInstallPrompt(null); });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  async function handleInstall() {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setInstalled(true);
    setInstallPrompt(null);
  }

  if (isAuthenticated) return null;

  return (
    <>
    <SEO
      title="Buck Euchre Online - Play Free"
      description="Play Buck Euchre online for free at Euchre Table. Challenge AI opponents or play multiplayer with friends. No download required. Join the largest Buck Euchre community."
      path="/"
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Euchre Table",
        "url": "https://www.euchretable.com",
        "description": "Play Buck Euchre online for free. Challenge AI opponents or compete with friends.",
        "publisher": {
          "@type": "Organization",
          "name": "Euchre Table",
          "url": "https://www.euchretable.com"
        }
      }}
    />
    <div style={{
      ...hardwoodFloorStyle,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      textAlign: 'center',
      padding: '20px'
    }}>
      <img
        src="/BuckEuchreLogoWhite.png"
        alt="Buck Euchre"
        style={{
          width: 800,
          maxWidth: '90vw',
          height: 'auto',
          marginBottom: 16,
          filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.5))'
        }}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%', maxWidth: 400 }}>
        {/* Choice 1: Create Account / Sign In */}
        <button
          onClick={() => navigate(isAuthenticated ? '/home' : '/signin')}
          style={{
            background: 'linear-gradient(135deg, #1c3d2a, #1a2f1a)',
            border: '2px solid #3fb950',
            borderRadius: 12,
            padding: '20px 24px',
            cursor: 'pointer',
            textAlign: 'center',
            width: '100%'
          }}
        >
          <div style={{
            color: '#fff',
            fontSize: 20,
            fontWeight: 700,
            fontFamily: "'Georgia', serif",
            letterSpacing: 1,
            marginBottom: 8
          }}>
            {isAuthenticated ? 'CONTINUE' : 'CREATE ACCOUNT / SIGN IN'}
          </div>
          <div style={{
            color: '#3fb950',
            fontSize: 15,
            fontFamily: "'Georgia', serif"
          }}>
            Verify your email for 100 free Bucks!
          </div>
        </button>

        {/* Install App button — only shown when browser supports PWA install */}
        {installPrompt && !installed && (
          <button
            onClick={handleInstall}
            style={{
              background: 'linear-gradient(135deg, #1a1a00, #2d2a00)',
              border: '2px solid #d4af37',
              borderRadius: 12,
              padding: '16px 24px',
              cursor: 'pointer',
              textAlign: 'center',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
            }}
          >
            <span style={{ fontSize: 22 }}>📲</span>
            <div>
              <div style={{ color: '#d4af37', fontSize: 18, fontWeight: 700, fontFamily: "'Georgia', serif", letterSpacing: 1 }}>
                INSTALL APP
              </div>
              <div style={{ color: '#a87e2a', fontSize: 13, fontFamily: "'Georgia', serif" }}>
                Play from your home screen — no browser needed
              </div>
            </div>
          </button>
        )}
        {installed && (
          <div style={{ color: '#d4af37', fontSize: 14, fontFamily: "'Georgia', serif", textAlign: 'center', padding: '8px 0' }}>
            ✅ App installed! Find it on your home screen.
          </div>
        )}

        {/* Choice 2: Free Play — commented out, sign-in required
        <button
          onClick={() => navigate('/play?mode=free')}
          style={{
            background: 'linear-gradient(135deg, #1e1b4b, #312e81)',
            border: '2px solid #6366f1',
            borderRadius: 12,
            padding: '20px 24px',
            cursor: 'pointer',
            textAlign: 'center',
            width: '100%'
          }}
        >
          <div style={{ color: '#fff', fontSize: 20, fontWeight: 700, fontFamily: "'Georgia', serif", letterSpacing: 1, marginBottom: 4 }}>
            FREE PLAY
          </div>
          <div style={{ color: '#a78bfa', fontSize: 15, fontFamily: "'Georgia', serif" }}>
            No account needed
          </div>
        </button>
        */}
      </div>

      {/* SEO Navigation Links */}
      <nav style={{
        marginTop: 32,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '8px 20px',
        maxWidth: 600
      }}>
        <Link to="/buck-euchre-rules" style={{ color: '#8b949e', fontSize: 14, fontFamily: "'Georgia', serif" }}>Rules</Link>
        <Link to="/buck-euchre-strategy" style={{ color: '#8b949e', fontSize: 14, fontFamily: "'Georgia', serif" }}>Strategy</Link>
        <Link to="/tutorial" style={{ color: '#8b949e', fontSize: 14, fontFamily: "'Georgia', serif" }}>Tutorial</Link>
        <Link to="/leaderboards" style={{ color: '#8b949e', fontSize: 14, fontFamily: "'Georgia', serif" }}>Leaderboards</Link>
        <Link to="/tournaments" style={{ color: '#8b949e', fontSize: 14, fontFamily: "'Georgia', serif" }}>Tournaments</Link>
        <Link to="/about" style={{ color: '#8b949e', fontSize: 14, fontFamily: "'Georgia', serif" }}>About</Link>
        <Link to="/fairness" style={{ color: '#8b949e', fontSize: 14, fontFamily: "'Georgia', serif" }}>Fairness</Link>
      </nav>
      <Footer isMobile={false} />
    </div>
    </>
  );
}
